import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Check } from 'lucide-react';
import type { GeneratedTask } from '../lib/supabase/aiTimelineService';

const CATEGORY_COLORS: Record<string, string> = {
  '場地': 'bg-blue-100 text-blue-700',
  '攝影': 'bg-purple-100 text-purple-700',
  '造型': 'bg-pink-100 text-pink-700',
  '邀請函': 'bg-rose-100 text-rose-700',
  '婚紗': 'bg-fuchsia-100 text-fuchsia-700',
  '禮品': 'bg-amber-100 text-amber-700',
  '流程': 'bg-green-100 text-green-700',
  '預算': 'bg-emerald-100 text-emerald-700',
  '賓客': 'bg-cyan-100 text-cyan-700',
  '交通': 'bg-orange-100 text-orange-700',
  '住宿': 'bg-teal-100 text-teal-700',
  '蜜月': 'bg-red-100 text-red-700',
  '文件': 'bg-slate-100 text-slate-700',
  '其他': 'bg-gray-100 text-gray-700',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

interface AITimelinePreviewProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: GeneratedTask[];
  onConfirm: (selectedTasks: GeneratedTask[]) => Promise<void>;
  isInserting: boolean;
}

const AITimelinePreview: React.FC<AITimelinePreviewProps> = ({
  isOpen,
  onClose,
  tasks,
  onConfirm,
  isInserting,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() =>
    new Set(tasks.map((_, i) => i))
  );

  React.useEffect(() => {
    setSelectedIds(new Set(tasks.map((_, i) => i)));
  }, [tasks]);

  const allSelected = selectedIds.size === tasks.length;
  const selectedCount = selectedIds.size;

  const selectedTasks = useMemo(
    () => tasks.filter((_, i) => selectedIds.has(i)),
    [tasks, selectedIds]
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tasks.map((_, i) => i)));
    }
  };

  const toggleTask = (index: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedTasks);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isInserting && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-wedding-pink" />
            AI 生成的時間軸
          </DialogTitle>
          <DialogDescription>
            已為您生成 {tasks.length} 個婚禮籌備任務，取消勾選不需要的項目
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          <div className="flex items-center justify-between py-2 mb-2 border-b sticky top-0 bg-white z-10">
            <button
              onClick={toggleAll}
              className="text-sm text-wedding-pink hover:underline"
              disabled={isInserting}
            >
              {allSelected ? '取消全選' : '全選'}
            </button>
            <span className="text-sm text-gray-500">
              已選 {selectedCount} / {tasks.length}
            </span>
          </div>

          <div className="space-y-2 pb-2">
            {tasks.map((task, index) => {
              const isSelected = selectedIds.has(index);
              const categoryColor = CATEGORY_COLORS[task.category] || CATEGORY_COLORS['其他'];

              return (
                <button
                  key={index}
                  onClick={() => toggleTask(index)}
                  disabled={isInserting}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? 'border-wedding-pink/30 bg-pink-50/50'
                      : 'border-gray-200 bg-gray-50/50 opacity-60'
                  } ${isInserting ? 'cursor-not-allowed' : 'hover:border-wedding-pink/50'}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'border-wedding-pink bg-wedding-pink'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-gray-500 font-mono">
                          {formatDate(task.target_date)}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor}`}
                        >
                          {task.category}
                        </span>
                        <span className="font-medium text-sm text-gray-900 truncate">
                          {task.title}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isInserting}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isInserting}
            className="bg-wedding-pink hover:bg-wedding-pink/90 text-white"
          >
            {isInserting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                新增中...
              </>
            ) : (
              <>
                確認加入 ({selectedCount} 個)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AITimelinePreview;
